import React from 'react';
import {
  ApplianceShell,
  ControlBand,
  HorizontalHandle,
  HorizontalSplit,
} from './ApplianceFrame';
import { resolveWallOvenVariant } from '@shared-lib/kitchen-assembly/ovenVariant';

// Single vs double is decided by the shared resolver so elevations, the 3D
// procedural renderer and kitchen post-processing can never disagree. Kitchen
// post-processing stamps `subtype` on scanned ovens; the height heuristic is
// the fallback for objects that predate it.
export { resolveWallOvenVariant };

const CONTROL_BAND_RATIO = 0.12;

export function WallOvenSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  if (view === 'left' || view === 'right') {
    return (
      <WallOvenSideView
        view={view}
        depthMeters={depthMeters}
        heightMeters={height}
        obj={obj}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const variant = resolveWallOvenVariant(obj, height);
  return (
    <ApplianceShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {variant === 'double' ? (
        <DoubleOvenBody
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ) : (
        <SingleOvenBody
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </ApplianceShell>
  );
}

WallOvenSchematic.supportsSideViews = true;

/**
 * Side elevation of a wall oven: simple rectangle (depth × height)
 * with an oven-door handle bar protruding slightly from the FRONT
 * edge. For a double-stacked variant, two handles — one per cavity.
 *
 * Convention: LEFT view places the front face at x = depthMeters
 * (right edge of the drawing). Mirrored for RIGHT.
 */
function WallOvenSideView({
  depthMeters,
  heightMeters,
  obj,
  strokeColor,
  strokeWidth,
  view,
}) {
  const w = depthMeters;
  const h = heightMeters;
  const variant = resolveWallOvenVariant(obj, h);
  const handleProtrusion = Math.min(w * 0.05, 0.02);
  const handleLen = Math.min(h * 0.012, 0.025);

  const handleYs =
    variant === 'double' ? [h * 0.08, h * 0.5 + h * 0.04] : [h * 0.12];

  const dividerY = variant === 'double' ? h / 2 : null;

  const handle = (handleY, key) => (
    <React.Fragment key={key}>
      <line
        x1={w}
        y1={handleY - handleLen / 2}
        x2={w + handleProtrusion}
        y2={handleY - handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={w + handleProtrusion}
        y1={handleY - handleLen / 2}
        x2={w + handleProtrusion}
        y2={handleY + handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={w + handleProtrusion}
        y1={handleY + handleLen / 2}
        x2={w}
        y2={handleY + handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </React.Fragment>
  );

  const sideChildren = (
    <>
      {dividerY != null && (
        <line
          x1={0}
          y1={dividerY}
          x2={w}
          y2={dividerY}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {handleYs.map((hy, i) => handle(hy, `h-${i}`))}
    </>
  );

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {view === 'right' ? (
        <g transform={`translate(${w}, 0) scale(-1, 1)`}>{sideChildren}</g>
      ) : (
        sideChildren
      )}
    </svg>
  );
}

function SingleOvenBody({ width, height, strokeColor, strokeWidth }) {
  const bandH = height * CONTROL_BAND_RATIO;
  const handleY = bandH + (height - bandH) * 0.08;
  return (
    <g>
      <ControlBand
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        ratio={CONTROL_BAND_RATIO}
        knobCount={4}
      />
      <HorizontalHandle
        cx={width / 2}
        y={handleY}
        length={width * 0.7}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Two ovens stacked vertically, each with its own control band and
 * handle. Upper oven's band sits at the top; lower oven's band sits
 * directly above the lower door (mid-height divider). The split is
 * 50/50 by default — most real double wall ovens are nearly even.
 */
function DoubleOvenBody({ width, height, strokeColor, strokeWidth }) {
  const midY = height / 2;
  const upperBandH = midY * CONTROL_BAND_RATIO * 2;
  const lowerBandH = (height - midY) * CONTROL_BAND_RATIO * 2;
  const upperHandleY = upperBandH + (midY - upperBandH) * 0.1;
  const lowerHandleY = midY + lowerBandH + (height - midY - lowerBandH) * 0.1;
  return (
    <g>
      {/* Upper oven control band */}
      <HorizontalSplit
        width={width}
        y={upperBandH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <HorizontalHandle
        cx={width / 2}
        y={upperHandleY}
        length={width * 0.7}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Divider between ovens */}
      <HorizontalSplit
        width={width}
        y={midY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Lower oven control band */}
      <HorizontalSplit
        width={width}
        y={midY + lowerBandH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <HorizontalHandle
        cx={width / 2}
        y={lowerHandleY}
        length={width * 0.7}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}
